export interface LocalStorageHelper {
  get(): string | null;
  set(data: string): void;
}

export interface ObjectToStore {
  data: string;
  ttl: number;
}

interface StorageAdapter {
  getItem(key: string): string | null;

  setItem(key: string, value: string): void;
}

export const localStorageAvailable = (window: Window): boolean => {
  try {
    const localStorageAvailable =
      window.localStorage !== undefined && window.localStorage !== null;
    if (localStorageAvailable) {
      // This can throw, we will catch it, but that's why we do not return
      // localStorageAvailable immediately
      window.localStorage.setItem("__test__", "test");
      window.localStorage.removeItem("__test__");
    }
    return localStorageAvailable;
  } catch {
    return false;
  }
};

export const localStorageHelper = (
  key: string,
  ttlInMilliseconds: number,
  container: Window,
): LocalStorageHelper => {
  const storageAvailable = localStorageAvailable(container);
  const storageAdapter: StorageAdapter = {
    getItem: (key: string): string | null =>
      storageAvailable ? container.localStorage.getItem(key) : null,
    setItem: (key: string, value: string): void => {
      if (storageAvailable) {
        return container.localStorage.setItem(key, value);
      }
    },
  };

  const expired = (ttl: number | undefined): boolean =>
    ttl ? ttl < Date.now() : true;

  const getObjectToStore = (data: string): ObjectToStore => ({
    data,
    ttl: Date.now() + ttlInMilliseconds,
  });

  const parseStoredValue = (storedValue: ObjectToStore): string | null =>
    expired(storedValue.ttl) ? null : storedValue.data;

  return {
    set: (data: string): void => {
      const objectToStore = getObjectToStore(data);
      storageAdapter.setItem(key, JSON.stringify(objectToStore));
    },
    get: (): string | null => {
      const storedValue: string | null = storageAdapter.getItem(key);
      return !storedValue ? null : parseStoredValue(JSON.parse(storedValue));
    },
  };
};
